import React, { useState } from 'react';
import { api } from '@/api/client';
import type { Source, SourceTestResult } from '@/types';
import { SourceChips, StatusBadge } from './ui';
import { useToast } from './Toast';

type Props = {
  source: Source;
  onClose: () => void;
};

export default function SourceTestModal({ source, onClose }: Props) {
  const { toast } = useToast();
  const [testUrl, setTestUrl] = useState('');
  const [searchText, setSearchText] = useState('BMW');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<SourceTestResult | null>(null);

  async function runTest() {
    setRunning(true);
    setResult(null);
    try {
      const data = await api.sources.test(source.id, {
        test_url: testUrl.trim() || undefined,
        search_text: testUrl.trim() ? undefined : searchText.trim() || undefined,
        limit: 10,
      });
      setResult(data);
      if (data.error) {
        toast(data.error, 'error');
      } else {
        toast(`Extracted ${data.links_count} link(s) in ${data.duration_seconds}s`, 'success');
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Test scrape failed', 'error');
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal card source-test-modal"
        role="dialog"
        aria-labelledby="source-test-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="source-test-title">Test scraper</h2>
        <div className="source-test-modal__header">
          <SourceChips sources={[source.name]} enabledBySource={{ [source.name]: source.is_enabled }} />
          <StatusBadge variant={source.is_enabled ? 'success' : 'warning'}>
            {source.is_enabled ? 'Enabled' : 'Disabled'}
          </StatusBadge>
        </div>

        <p className="muted">
          Runs a bounded fetch and link extraction without saving to the database. Leave URL empty to build a search URL
          from search text.
        </p>

        <div className="admin-form-grid admin-form-grid--sources">
          <label className="admin-form-grid__span-2">
            Test URL (optional)
            <input
              value={testUrl}
              onChange={(e) => setTestUrl(e.target.value)}
              placeholder={source.base_url}
            />
          </label>
          <label className="admin-form-grid__span-2">
            Search text (when URL empty)
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              disabled={!!testUrl.trim()}
            />
          </label>
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-primary" disabled={running} onClick={() => void runTest()}>
            {running ? 'Running test…' : 'Run test scrape'}
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
        </div>

        {result ? (
          <div className="source-test-modal__results">
            <dl className="detail-grid">
              <dt>Seed URL</dt>
              <dd className="break-all">{result.seed_url || '—'}</dd>
              <dt>Fetch mode</dt>
              <dd>{result.fetch_mode}</dd>
              <dt>Duration</dt>
              <dd>{result.duration_seconds}s</dd>
              <dt>HTTP status</dt>
              <dd>{result.http_status ?? '—'}</dd>
              <dt>Links extracted</dt>
              <dd>{result.links_count}</dd>
              <dt>Candidate cards</dt>
              <dd>{result.candidate_cards}</dd>
              <dt>Page title</dt>
              <dd>{result.page_title || '—'}</dd>
              {result.error ? (
                <>
                  <dt>Error</dt>
                  <dd className="text-danger">{result.error}</dd>
                </>
              ) : null}
              {result.block_markers.length > 0 ? (
                <>
                  <dt>Block markers</dt>
                  <dd>{result.block_markers.join(', ')}</dd>
                </>
              ) : null}
              {result.diagnosis ? (
                <>
                  <dt>Diagnosis</dt>
                  <dd>{result.diagnosis}</dd>
                </>
              ) : null}
            </dl>
            {result.sample_links.length > 0 ? (
              <>
                <h3>Sample links</h3>
                <ul className="metric-list">
                  {result.sample_links.map((link) => (
                    <li key={link.url}>
                      <a href={link.url} target="_blank" rel="noopener noreferrer">
                        {link.title || link.url}
                      </a>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
